/**
 * 웹뷰 인앱 결제 유틸리티
 * React Native WebView 브릿지를 통해 네이티브 앱의 인앱 결제 기능을 호출합니다.
 */

// React Native WebView 타입 정의
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

/**
 * 플랫폼 감지
 * @returns 'ios' | 'android'
 */
export function detectPlatform(): 'ios' | 'android' {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios'
  }
  
  if (/android/i.test(userAgent)) {
    return 'android'
  }
  
  return 'ios' // 기본값
}

/**
 * 인앱 결제 시작
 * React Native WebView 브릿지를 통해 네이티브 인앱 결제를 호출합니다.
 * @param productId - 상품 ID (예: "point_1000", "point_5000")
 * @param amount - 포인트 금액
 * @returns Promise<string> - 결제 토큰
 */
export function startInAppPurchase(productId: string, amount: number): Promise<string> {
  return new Promise((resolve, reject) => {
    // 5분 타임아웃 설정
    const timeout = setTimeout(() => {
      if ((window as any).purchaseCallback) {
        delete (window as any).purchaseCallback
      }
      reject(new Error('결제 시간이 초과되었습니다. 다시 시도해주세요.'))
    }, 5 * 60 * 1000)
    
    // 결제 완료 콜백 등록
    ;(window as any).purchaseCallback = (token: string | null) => {
      clearTimeout(timeout)
      delete (window as any).purchaseCallback // 콜백 정리
      
      if (token) {
        resolve(token)
      } else {
        reject(new Error('결제가 취소되었습니다.'))
      }
    }
    
    const platform = detectPlatform()
    
    // React Native WebView 브릿지 사용
    if (window.ReactNativeWebView) {
      // 네이티브로 결제 요청 전송
      const message = JSON.stringify({
        type: 'PURCHASE',
        productId: productId,
        amount: amount,
        platform: platform,
      })
      window.ReactNativeWebView.postMessage(message)
    }
    // iOS WebKit 메시지 핸들러 (fallback)
    else if (
      (window as any).webkit &&
      (window as any).webkit.messageHandlers &&
      (window as any).webkit.messageHandlers.purchase
    ) {
      // 네이티브로 결제 요청 전송
      ;(window as any).webkit.messageHandlers.purchase.postMessage({
        productId: productId,
        amount: amount,
      })
    }
    // Android WebView JavaScript Interface (fallback)
    else if ((window as any).Android && typeof (window as any).Android.purchase === 'function') {
      // 네이티브로 결제 요청 전송
      ;(window as any).Android.purchase(productId, amount)
    }
    else {
      clearTimeout(timeout)
      delete (window as any).purchaseCallback
      reject(new Error('인앱 결제를 사용할 수 없습니다. 앱에서 실행해주세요.'))
    }
  })
}

/**
 * 보상형 광고 시청 요청
 * 네이티브에서 보상형 광고를 표시하고, 시청 완료 시 플랫폼 정보로 resolve합니다.
 * 완료 후 웹에서 `pointApi.chargePoint`로 충전 요청하면 됩니다.
 * @returns Promise<{ platform: 'ios' | 'android' }> - 광고 시청 완료 시 플랫폼
 */
export function requestRewardedAd(): Promise<{ platform: 'ios' | 'android' }> {
  return new Promise((resolve, reject) => {
    // 콜백이 누락된 비정상 상황을 빠르게 종료해 사용자 대기 시간을 줄입니다.
    const timeout = setTimeout(() => {
      if ((window as any).rewardedAdCallback) {
        delete (window as any).rewardedAdCallback
      }
      reject(new Error('광고가 준비되지 않아 표시할 수 없습니다.'))
    }, 300 * 1000)

    ;(window as any).rewardedAdCallback = (platform: 'ios' | 'android' | null, error: string | null) => {
      clearTimeout(timeout)
      delete (window as any).rewardedAdCallback
      if (error || !platform) {
        reject(new Error(error || '광고 시청이 완료되지 않았습니다.'))
      } else {
        resolve({ platform })
      }
    }

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'REQUEST_REWARDED_AD' })
      )
    } else {
      clearTimeout(timeout)
      delete (window as any).rewardedAdCallback
      reject(new Error('보상형 광고는 앱에서만 시청할 수 있습니다.'))
    }
  })
}

/**
 * 웹뷰 환경인지 확인
 * @returns boolean
 */
export function isWebView(): boolean {
  // React Native WebView 우선 확인
  if (window.ReactNativeWebView) {
    return true
  }
  
  const platform = detectPlatform()
  
  if (platform === 'ios') {
    return !!(
      (window as any).webkit &&
      (window as any).webkit.messageHandlers &&
      (window as any).webkit.messageHandlers.purchase
    )
  }
  
  if (platform === 'android') {
    return !!(window as any).Android && typeof (window as any).Android.purchase === 'function'
  }
  
  return false
}

